import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  ensurePusher,
  isPusherAuthSkipped,
  subscribeChannel,
  unsubscribeChannel,
} from "../services/pusher"
import {
  CLIENT_MESSAGE_DELETED_EVENT,
  CLIENT_MESSAGE_EVENT,
  CLIENT_REACTION_EVENT,
  CLIENT_TYPING_EVENT,
  MESSAGE_REACTION_EVENT,
  MESSAGE_REACTION_REMOVED_EVENT,
  MESSAGE_SENT_EVENT,
  MESSAGE_UPDATED_EVENT,
  TYPING_EXPIRE_MS,
  TYPING_THROTTLE_MS,
  eventNameAliases,
  getRealtimeChannelNames,
  getRealtimeMessageId,
  looksLikeDeletedEvent,
  looksLikeMessageSentEvent,
  looksLikeMessageUpdatedEvent,
  looksLikeReactionEvent,
  looksLikeTypingEvent,
  unwrapRealtimePayload,
} from "../constants/messagesRealtime"
import { getUserFullName } from "../helpers/userDisplay"

const formatPersonName = user => getUserFullName(user)

const bindNamedEvents = (target, names, handler) => {
  if (!target || typeof target.bind !== "function") return
  names.forEach(name => target.bind(name, handler))
}

const unbindNamedEvents = (target, names, handler) => {
  if (!target || typeof target.unbind !== "function") return
  names.forEach(name => target.unbind(name, handler))
}

const REACTION_ADDED_EVENTS = eventNameAliases(MESSAGE_REACTION_EVENT)
const REACTION_REMOVED_EVENTS = eventNameAliases(MESSAGE_REACTION_REMOVED_EVENT)
const MESSAGE_SENT_EVENTS = eventNameAliases(MESSAGE_SENT_EVENT).concat(
  eventNameAliases(CLIENT_MESSAGE_EVENT),
)
const MESSAGE_UPDATED_EVENTS = eventNameAliases(MESSAGE_UPDATED_EVENT)

const triggerClientEvent = (channel, eventName, payload) => {
  if (!channel || typeof channel.trigger !== "function") return
  // Client events only work on private/presence channels with backend auth.
  // When auth is skipped (public channels), we cannot broadcast from the client.
  if (isPusherAuthSkipped()) return
  try {
    channel.trigger(eventName, payload)
  } catch {
    // Public channels cannot emit client events.
  }
}

const resolveTypingUser = (payload, currentUserId) => {
  const data = unwrapRealtimePayload(payload) || {}
  const user = data.user || data.sender || payload?.user || {}
  const userId = String(
    data.user_id ||
      data.userId ||
      data.sender_id ||
      user.id ||
      payload?.user_id ||
      "",
  )
  if (!userId || userId === String(currentUserId || "")) return null

  const typingValue = data.typing ?? data.is_typing ?? payload?.typing
  const stopped =
    data.stopped === true ||
    data.action === "stopped" ||
    data.type === "stopped" ||
    typingValue === false

  const name =
    formatPersonName(user) ||
    data.user_name ||
    data.name ||
    payload?.user_name ||
    "Someone"

  return { userId, name, stopped }
}

const useChannelSubscriptions = (channelNames, onChannel) => {
  const onChannelRef = useRef(onChannel)
  onChannelRef.current = onChannel
  const namesKey = (channelNames || []).filter(Boolean).join("|")

  useEffect(() => {
    const names = namesKey ? namesKey.split("|") : []
    if (names.length === 0) return undefined

    const pusher = ensurePusher()
    if (!pusher) return undefined

    const channels = names.map(name => subscribeChannel(name)).filter(Boolean)

    const attach = () => {
      channels.forEach(channel => {
        if (channel && !channel.subscribed) {
          pusher.subscribe(channel.name)
        }
        onChannelRef.current?.(channel)
      })
    }

    attach()
    pusher.connection.bind("connected", attach)

    return () => {
      pusher.connection.unbind("connected", attach)
      channels.forEach(channel => {
        onChannelRef.current?.(null, channel)
        unsubscribeChannel(channel.name)
      })
    }
  }, [namesKey])
}

/**
 * Real-time layer for a project conversation.
 * Updates local thread/sidebar state only — never refetches REST lists.
 */
const useMessageRealtime = ({
  contextId,
  projectId,
  currentUser,
  conversationRaw,
  enabled = true,
  onMessage,
  onMessageUpdated,
  onMessageDeleted,
  onReaction,
} = {}) => {
  const [typingUsers, setTypingUsers] = useState({})
  const [connectionState, setConnectionState] = useState(
    () => ensurePusher()?.connection?.state || "disconnected",
  )
  const contextChannelRef = useRef(null)
  const typingTimerRef = useRef(null)
  const lastTypingSentRef = useRef(0)
  const typingExpireTimersRef = useRef({})
  const seenReactionKeysRef = useRef(new Set())
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : ""

  const callbacksRef = useRef({
    onMessage,
    onMessageUpdated,
    onMessageDeleted,
    onReaction,
  })
  callbacksRef.current = {
    onMessage,
    onMessageUpdated,
    onMessageDeleted,
    onReaction,
  }

  const channelNames = useMemo(
    () =>
      enabled
        ? getRealtimeChannelNames({
            contextId,
            projectId,
            userId: currentUserId,
            conversationRaw,
          })
        : [],
    [enabled, contextId, projectId, currentUserId, conversationRaw],
  )

  const clearTypingUser = useCallback(userId => {
    if (!userId) return
    const timer = typingExpireTimersRef.current[userId]
    if (timer) {
      window.clearTimeout(timer)
      delete typingExpireTimersRef.current[userId]
    }
    setTypingUsers(prev => {
      if (!prev[userId]) return prev
      const next = { ...prev }
      delete next[userId]
      return next
    })
  }, [])

  const applyTyping = useCallback(
    payload => {
      const typing = resolveTypingUser(payload, currentUserId)
      if (!typing) return
      if (typing.stopped) {
        clearTypingUser(typing.userId)
        return
      }

      setTypingUsers(prev => ({
        ...prev,
        [typing.userId]: { id: typing.userId, name: typing.name },
      }))

      const existing = typingExpireTimersRef.current[typing.userId]
      if (existing) window.clearTimeout(existing)
      typingExpireTimersRef.current[typing.userId] = window.setTimeout(() => {
        clearTypingUser(typing.userId)
      }, TYPING_EXPIRE_MS)
    },
    [clearTypingUser, currentUserId],
  )

  const handleCreated = useCallback(
    payload => {
      const data = unwrapRealtimePayload(payload) || {}
      const senderId = String(
        data.sender_id ||
          data.user_id ||
          data.sender?.id ||
          data.user?.id ||
          "",
      )
      if (senderId) clearTypingUser(senderId)
      callbacksRef.current.onMessage?.(payload)
    },
    [clearTypingUser],
  )

  const handleUpdated = useCallback(payload => {
    callbacksRef.current.onMessageUpdated?.(payload)
  }, [])

  const handleDeleted = useCallback(payload => {
    const id = getRealtimeMessageId(payload)
    if (id) callbacksRef.current.onMessageDeleted?.(id, payload)
  }, [])

  const handleReaction = useCallback(
    (payload, eventName = "") => {
      const data = unwrapRealtimePayload(payload) || {}
      const messageId =
        data.message_id || data.messageId || getRealtimeMessageId(payload)
      const reaction = data.reaction || data.emoji || payload?.reaction || ""
      const userId = String(
        data.user_id ||
          data.userId ||
          data.sender?.id ||
          data.user?.id ||
          payload?.user_id ||
          "",
      )
      const reactionSummaryRaw = Array.isArray(data.reaction_summary)
        ? data.reaction_summary
        : Array.isArray(data.reactions)
          ? data.reactions
          : null
      // Empty arrays are not usable summaries — treat as missing so we don't
      // wipe local chips or skip peer increments incorrectly.
      const reactionSummary =
        Array.isArray(reactionSummaryRaw) && reactionSummaryRaw.length > 0
          ? reactionSummaryRaw
          : null
      const eventAction =
        eventName === MESSAGE_REACTION_REMOVED_EVENT ||
        String(eventName).toLowerCase().includes("removed")
          ? "removed"
          : ""
      const action = String(
        data.action || data.type || payload?.action || eventAction,
      ).toLowerCase()
      if (userId && userId === currentUserId && !reactionSummary) return
      const key = `${messageId}:${userId || "anon"}:${reaction}:${
        action || "sync"
      }:${JSON.stringify(reactionSummary || [])}`
      if (seenReactionKeysRef.current.has(key)) return
      seenReactionKeysRef.current.add(key)
      if (seenReactionKeysRef.current.size > 200) {
        const first = seenReactionKeysRef.current.values().next().value
        seenReactionKeysRef.current.delete(first)
      }
      callbacksRef.current.onReaction?.({
        payload,
        messageId,
        reaction,
        userId,
        action,
        reactionSummary,
      })
    },
    [currentUserId],
  )

  const handleReactionRemoved = useCallback(
    payload => handleReaction(payload, MESSAGE_REACTION_REMOVED_EVENT),
    [handleReaction],
  )

  const onGlobalEvent = useCallback(
    (eventName, payload) => {
      if (!eventName || String(eventName).startsWith("pusher:")) return

      // Typing is purely ephemeral UI state — never treated as a message.
      if (looksLikeTypingEvent(eventName, payload)) {
        applyTyping(payload)
        return
      }

      // message.sent / message.updated must win over payload sniffing.
      // Backend includes reaction_summary: [] on every sent message.
      if (looksLikeMessageSentEvent(eventName)) {
        handleCreated(payload)
        return
      }
      if (looksLikeMessageUpdatedEvent(eventName)) {
        handleUpdated(payload)
        return
      }

      if (
        eventName === MESSAGE_REACTION_EVENT ||
        eventName === MESSAGE_REACTION_REMOVED_EVENT ||
        looksLikeReactionEvent(eventName, payload)
      ) {
        handleReaction(payload, eventName)
        return
      }

      if (looksLikeDeletedEvent(eventName)) {
        const id = getRealtimeMessageId(payload)
        if (id) {
          handleDeleted(payload)
          return
        }
      }

      handleCreated(payload)
    },
    [applyTyping, handleCreated, handleDeleted, handleReaction, handleUpdated],
  )

  const wireChannel = useCallback(
    (channel, shouldBind) => {
      if (!channel) return
      unbindNamedEvents(channel, MESSAGE_SENT_EVENTS, handleCreated)
      unbindNamedEvents(channel, MESSAGE_UPDATED_EVENTS, handleUpdated)
      unbindNamedEvents(channel, REACTION_ADDED_EVENTS, handleReaction)
      unbindNamedEvents(channel, REACTION_REMOVED_EVENTS, handleReactionRemoved)
      if (typeof channel.unbind_global === "function") {
        channel.unbind_global(onGlobalEvent)
      }
      if (shouldBind) {
        bindNamedEvents(channel, MESSAGE_SENT_EVENTS, handleCreated)
        bindNamedEvents(channel, MESSAGE_UPDATED_EVENTS, handleUpdated)
        bindNamedEvents(channel, REACTION_ADDED_EVENTS, handleReaction)
        bindNamedEvents(channel, REACTION_REMOVED_EVENTS, handleReactionRemoved)
        if (typeof channel.bind_global === "function") {
          channel.bind_global(onGlobalEvent)
        }
      }
    },
    [
      handleCreated,
      handleReaction,
      handleReactionRemoved,
      handleUpdated,
      onGlobalEvent,
    ],
  )

  useChannelSubscriptions(channelNames, (channel, unmountChannel) => {
    if (unmountChannel) {
      if (
        contextChannelRef.current &&
        unmountChannel.name === contextChannelRef.current.name
      ) {
        triggerClientEvent(unmountChannel, CLIENT_TYPING_EVENT, {
          user_id: currentUserId,
          user_name: formatPersonName(currentUser) || "Someone",
          typing: false,
          context_id: contextId,
        })
        contextChannelRef.current = null
      }
      wireChannel(unmountChannel, false)
      return
    }

    if (
      channel &&
      contextId &&
      String(channel.name).includes(String(contextId))
    ) {
      contextChannelRef.current = channel
    }
    wireChannel(channel, true)
  })

  useEffect(() => {
    const pusher = ensurePusher()
    if (!pusher) return undefined

    bindNamedEvents(pusher, REACTION_ADDED_EVENTS, handleReaction)
    bindNamedEvents(pusher, REACTION_REMOVED_EVENTS, handleReactionRemoved)

    const applyState = state => {
      setConnectionState(state || pusher.connection?.state || "disconnected")
    }
    applyState(pusher.connection?.state)
    const handleStateChange = states => applyState(states?.current)
    pusher.connection.bind("state_change", handleStateChange)

    return () => {
      unbindNamedEvents(pusher, REACTION_ADDED_EVENTS, handleReaction)
      unbindNamedEvents(pusher, REACTION_REMOVED_EVENTS, handleReactionRemoved)
      pusher.connection.unbind("state_change", handleStateChange)
    }
  }, [handleReaction, handleReactionRemoved])

  useEffect(
    () => () => {
      if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current)
      Object.values(typingExpireTimersRef.current).forEach(timer => {
        window.clearTimeout(timer)
      })
      typingExpireTimersRef.current = {}
    },
    [],
  )

  const sendTyping = useCallback(
    isTyping => {
      const channel = contextChannelRef.current
      if (!channel || !currentUserId) return

      const payload = {
        user_id: currentUserId,
        user_name: formatPersonName(currentUser) || "Someone",
        typing: Boolean(isTyping),
        context_id: contextId,
      }

      if (!isTyping) {
        lastTypingSentRef.current = 0
        triggerClientEvent(channel, CLIENT_TYPING_EVENT, payload)
        return
      }

      const now = Date.now()
      if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return
      lastTypingSentRef.current = now
      triggerClientEvent(channel, CLIENT_TYPING_EVENT, payload)
    },
    [contextId, currentUser, currentUserId],
  )

  const notifyTyping = useCallback(
    isTyping => {
      if (typingTimerRef.current) {
        window.clearTimeout(typingTimerRef.current)
        typingTimerRef.current = null
      }

      if (!isTyping) {
        sendTyping(false)
        return
      }

      sendTyping(true)
      typingTimerRef.current = window.setTimeout(() => {
        sendTyping(false)
      }, TYPING_EXPIRE_MS)
    },
    [sendTyping],
  )

  const notifyMessageChanged = useCallback(
    rawMessage => {
      const channel = contextChannelRef.current
      if (!channel || !rawMessage) return
      triggerClientEvent(channel, CLIENT_MESSAGE_EVENT, {
        ...rawMessage,
        context_id: rawMessage.context_id || contextId,
      })
    },
    [contextId],
  )

  const notifyMessageDeleted = useCallback(
    messageId => {
      const channel = contextChannelRef.current
      if (!channel || !messageId) return
      triggerClientEvent(channel, CLIENT_MESSAGE_DELETED_EVENT, {
        id: messageId,
        user_id: currentUserId,
        context_id: contextId,
      })
    },
    [contextId, currentUserId],
  )

  const notifyReaction = useCallback(
    ({ messageId, reaction, action = "added" }) => {
      const channel = contextChannelRef.current
      if (!channel || !messageId || !reaction) return
      triggerClientEvent(channel, CLIENT_REACTION_EVENT, {
        message_id: messageId,
        reaction,
        action,
        user_id: currentUserId,
        context_id: contextId,
      })
    },
    [contextId, currentUserId],
  )

  const typingLabel = useMemo(() => {
    const people = Object.values(typingUsers)
    if (people.length === 0) return ""
    if (people.length === 1) return `${people[0].name} is typing...`
    if (people.length === 2) {
      return `${people[0].name} and ${people[1].name} are typing...`
    }
    return `${people[0].name} and ${people.length - 1} others are typing...`
  }, [typingUsers])

  return {
    typingUsers,
    typingLabel,
    notifyTyping,
    notifyMessageChanged,
    notifyMessageDeleted,
    notifyReaction,
    channelNames,
    connectionState,
  }
}

export default useMessageRealtime
